import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { chain, first, isEmpty, isNil, keys } from 'lodash';
// Import Types
import type {
    DropdownOption,
    DynamicVariableChannelData,
    WhatsAppPlaceholderMapping,
    WhatsappTemplateApiResponse,
} from 'types';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import {
    batchChangeTemplateValueForAllLanguages,
    changeTemplateValue,
    selectTemplateState,
} from 'store/slices/template';
import {
    clearWhatsAppTemplateByLanguage,
    selectWhatsAppTemplateState,
    setWhatsAppTemplateByLanguage,
    setWhatsAppTemplateByLanguageLoading,
} from 'store/slices/whatsAppTemplate';
// Import Components
import { Alert, Dropdown } from '@geneui/components';
import TemplateCategoryInput from '../TemplateCategoryInput';
import WhatsAppPlaceholderMappingSection from './WhatsAppPlaceholderMappingSection';
import WhatsAppTemplatePreview from './WhatsAppTemplatePreview/WhatsAppTemplatePreview';
import SectionWithTitle from 'components/common/SectionWithTitle';
import { TemplateHttpService } from 'services/http';
import { parseSanitizedHtml } from 'services/html';
// Import Hooks
import useBind from 'hooks/UseBind';
import useRequest from 'hooks/UseRequest';
import useWhatsAppTemplateValidation from './useWhatsAppTemplateValidation';
// Import Enums
import { TemplateTypeEnum } from 'enums';
// Import Constants
import { l, TemplateInputTypes } from 'constants/common';
import { DefaultLanguage } from 'constants/template';
// Import SCSS
import 'assets/scss/socialNotification.scss';
import styles from './whatsAppTemplateContent.module.scss';

interface WhatsAppTemplateContentProps {
    templateData: AnyObject;
    templateLangKey: string;
    dynamicVariables?: DynamicVariableChannelData[];
    handleChange: (propertyName: string, value: unknown, changeTimeout?: number) => void;
}

const WhatsAppTemplateContent: React.FC<WhatsAppTemplateContentProps> = (props) => {
    const { templateData, templateLangKey, dynamicVariables = [], handleChange } = props;
    const { t } = useTranslation();
    const { doGetRequest } = useRequest();
    const dispatch = useAppDispatch();

    const { whatsAppTemplateList, templatesByLanguage, isLoadingTemplatesByLanguage } =
        useAppSelector(selectWhatsAppTemplateState);

    const allTemplateData = useAppSelector(selectTemplateState);
    const defaultLanguageTemplate = useAppSelector(selectTemplateState)[DefaultLanguage];

    const whatsAppTemplateContentAlertText = useMemo(() => parseSanitizedHtml(t(l.WhatsAppTemplateContentAlert)), [t]);
    const templateLanguageKeys = useMemo(() => keys(allTemplateData), [allTemplateData]);

    const { getWhatsAppTemplates } = useMemo(
        () => ({
            // eslint-disable-next-line no-restricted-syntax -- an abort would leave the isLoadingTemplatesByLanguage store flag stuck
            getWhatsAppTemplates: TemplateHttpService.getWhatsAppTemplates(),
        }),
        [],
    );

    const selectedTemplateByLanguage = useMemo<Nullable<WhatsappTemplateApiResponse>>(
        () =>
            (templatesByLanguage ?? []).find(
                (template) =>
                    template?.Language === templateData?.[TemplateInputTypes.ExternalWhatsAppTemplateLanguage],
            ) ?? null,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [templatesByLanguage, templateData?.[TemplateInputTypes.ExternalWhatsAppTemplateLanguage]],
    );

    const dynamicVariableOptions = useMemo(() => {
        if (isEmpty(dynamicVariables)) return [];

        return dynamicVariables
            .filter((item) => item?.channels?.[TemplateTypeEnum.WhatsApp]?.FieldName)
            .map((item) => ({
                label: t(item.displayNameKey),
                value: `{${item.channels[TemplateTypeEnum.WhatsApp].FieldName}}`,
            }));
    }, [dynamicVariables, t]);

    const whatsAppTemplateLanguageOptions = useMemo(() => {
        return chain(templatesByLanguage ?? [])
            .map('Language')
            .compact()
            .sort()
            .map((lang) => ({ label: l[lang] ? t(l[lang]) : lang, value: lang }))
            .value();
    }, [templatesByLanguage, t]);

    const handleCategoryChange = useBind(handleChange, TemplateInputTypes.Category);

    const selectedTemplateName = defaultLanguageTemplate?.[TemplateInputTypes.ExternalWhatsAppTemplateId] ?? null;
    const isDefaultLanguage = templateLangKey === DefaultLanguage;
    const categoryValue = templateData?.[TemplateInputTypes.Category];

    const {
        validator,
        languageValidationState,
        languageDetailsValidationState,
        templateValidationState,
        getLanguageValidationPayload,
        getLanguageDetailsValidationPayload,
        getPlaceholderMappingValidationPayload,
    } = useWhatsAppTemplateValidation({
        templateLangKey,
        templateLanguageKeys,
        allTemplateData,
        templateData,
        selectedTemplateName,
        selectedTemplateByLanguage: selectedTemplateByLanguage,
        templatesByLanguage: templatesByLanguage ?? null,
    });
    const isLanguageValid = languageValidationState.isValid && languageDetailsValidationState.isValid;
    const languageErrorText = languageValidationState.errorText || languageDetailsValidationState.errorText;

    useEffect(() => {
        const firstTemplate = first(templatesByLanguage) as Nilable<{ Name?: string }>;
        if (!selectedTemplateName || !isDefaultLanguage || firstTemplate?.Name === selectedTemplateName) {
            return;
        }

        dispatch(setWhatsAppTemplateByLanguageLoading(true));
        dispatch(setWhatsAppTemplateByLanguage(null));
        doGetRequest(getWhatsAppTemplates.request, {
            queryString: { templateName: selectedTemplateName },
            successCallback: (data) => {
                dispatch(setWhatsAppTemplateByLanguage(data));
                dispatch(setWhatsAppTemplateByLanguageLoading(false));
            },
            errorCallback: () => {
                dispatch(setWhatsAppTemplateByLanguageLoading(false));
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTemplateName]);

    const handleTemplateChange = (option: Nullable<DropdownOption<string>>) => {
        if (!isDefaultLanguage) return;

        const newTemplateId = option?.value ?? null;
        if (newTemplateId === selectedTemplateName) return;

        dispatch(clearWhatsAppTemplateByLanguage());
        dispatch(
            batchChangeTemplateValueForAllLanguages([
                {
                    propertyName: TemplateInputTypes.ExternalWhatsAppTemplateLanguage,
                    value: null,
                },
                {
                    propertyName: TemplateInputTypes.ExternalWhatsAppTemplatePlaceholderMapping,
                    value: null,
                },
                {
                    propertyName: TemplateInputTypes.ExternalWhatsAppTemplateId,
                    value: newTemplateId,
                },
            ]),
        );

        // Sync validation for all language tabs so save is blocked when any tab is invalid
        const validationPayload = templateLanguageKeys.reduce<Record<string, unknown>>((acc, langKey) => {
            acc[langKey + TemplateInputTypes.ExternalWhatsAppTemplateLanguage] = getLanguageValidationPayload(null);
            acc[langKey + TemplateInputTypes.ExternalWhatsAppTemplateDetails] = getLanguageDetailsValidationPayload(
                null,
                undefined,
            );
            acc[langKey + TemplateInputTypes.ExternalWhatsAppTemplateId] = newTemplateId;
            acc[langKey + TemplateInputTypes.ExternalWhatsAppTemplatePlaceholderMapping] =
                getPlaceholderMappingValidationPayload(null, null);
            return acc;
        }, {});
        validator(validationPayload);
    };

    const handleLanguageChange = (e) => {
        const newLanguage = e?.value ?? null;
        if (newLanguage === templateData?.[TemplateInputTypes.ExternalWhatsAppTemplateLanguage]) return;

        dispatch(
            changeTemplateValue({
                propertyName: TemplateInputTypes.ExternalWhatsAppTemplatePlaceholderMapping,
                value: null,
                templateLangKey,
            }),
        );
        handleChange(TemplateInputTypes.ExternalWhatsAppTemplateLanguage, newLanguage, 0);
    };

    const handleMappingChange = (mapping: Nullable<WhatsAppPlaceholderMapping>) => {
        dispatch(
            changeTemplateValue({
                propertyName: TemplateInputTypes.ExternalWhatsAppTemplatePlaceholderMapping,
                value: mapping,
                templateLangKey,
            }),
        );
        validator({
            [templateLangKey + TemplateInputTypes.ExternalWhatsAppTemplatePlaceholderMapping]:
                getPlaceholderMappingValidationPayload(selectedTemplateByLanguage, mapping),
        });
    };

    return (
        <div className={styles.mainContainer}>
            <div className={styles.inputsContainer}>
                <div className={styles.dropdownsContainer}>
                    {templateLangKey === DefaultLanguage && (
                        <TemplateCategoryInput
                            value={categoryValue}
                            onChange={handleCategoryChange}
                            wrapWithDiv={false}
                        />
                    )}
                    <Dropdown
                        data={whatsAppTemplateList}
                        value={selectedTemplateName}
                        onChange={handleTemplateChange}
                        disabled={!isDefaultLanguage}
                        labelAppearance="swap"
                        label={t(l.Template)}
                        placeholder={t(l.Template)}
                        isValid={templateValidationState.isValid}
                        errorText={templateValidationState.errorText}
                    />
                    <Dropdown
                        data={whatsAppTemplateLanguageOptions}
                        value={templateData?.[TemplateInputTypes.ExternalWhatsAppTemplateLanguage]}
                        onChange={handleLanguageChange}
                        disabled={!selectedTemplateName}
                        labelAppearance="swap"
                        label={t(l.Language)}
                        placeholder={t(l.Language)}
                        isValid={selectedTemplateName ? isLanguageValid : true}
                        errorText={selectedTemplateName ? languageErrorText : ''}
                        isLoading={isLoadingTemplatesByLanguage}
                    />
                </div>
                {isNil(selectedTemplateName) && (
                    <Alert
                        title={<div className="crm-text-from-html">{whatsAppTemplateContentAlertText}</div>}
                        iconProps={{ type: 'bc-icon-info' }}
                    />
                )}
                {!isNil(selectedTemplateByLanguage) && isLanguageValid && (
                    <WhatsAppPlaceholderMappingSection
                        selectedTemplateByLanguage={selectedTemplateByLanguage}
                        dynamicVariableOptions={dynamicVariableOptions}
                        value={templateData?.[TemplateInputTypes.ExternalWhatsAppTemplatePlaceholderMapping]}
                        onChange={handleMappingChange}
                    />
                )}
            </div>
            <div className={styles.selectedTemplatePreviewContainer}>
                <SectionWithTitle
                    title={t(l.Preview)}
                    className={styles.selectedTemplatePreviewSection}
                    contentClassName={styles.previewContent}
                >
                    {!isNil(selectedTemplateByLanguage) && (
                        <WhatsAppTemplatePreview components={selectedTemplateByLanguage?.Components} />
                    )}
                </SectionWithTitle>
            </div>
        </div>
    );
};

export default WhatsAppTemplateContent;
